import React, { useEffect, useMemo, useState } from 'react';
import {
    ActivityIndicator,
    Image,
    Modal,
    ScrollView,
    StyleSheet,
    Text,
    TextInput,
    TouchableOpacity,
    View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Clipboard from 'expo-clipboard';
import twoFactorAuth from '../services/twoFactorAuth';
import { COLORS, OtpInput, TwoFactorBrand } from './twoFactor/TwoFactorUI';
import RecoveryCodes from './twoFactor/RecoveryCodes';
import { ModalKeyboardAvoider } from './KeyboardAware';

const METHODS = ['totp', 'email', 'sms'];

/**
 * Pull the raw base32 seed out of an `otpauth://` provisioning URI so it can be
 * shown as a manual-entry fallback when the QR code can't be scanned.
 * @param {string} otpauth
 * @returns {string} base32 secret (or '')
 */
const extractTotpSecret = (otpauth) => {
    const match = /[?&]secret=([^&]+)/i.exec(String(otpauth || ''));

    return match ? decodeURIComponent(match[1]) : '';
};

/** Group a base32 secret into 4-char blocks for readability: ABCD EFGH … */
const formatSecretForDisplay = (secret) => String(secret || '').replace(/(.{4})/g, '$1 ').trim();

const METHOD_META = (t) => ({
    totp: {
        icon: 'keypad-outline',
        label: t('two_factor_method_auth_app', { defaultValue: 'Authenticator app' }),
        hint: t('two_factor_method_auth_app_help', { defaultValue: 'Most secure. Google Authenticator, 1Password, Authy.' }),
    },
    email: {
        icon: 'mail-outline',
        label: t('two_factor_method_email', { defaultValue: 'Email code' }),
        hint: t('two_factor_method_email_help', { defaultValue: 'Receive a one-time code in your inbox.' }),
    },
    sms: {
        icon: 'chatbubble-ellipses-outline',
        label: t('two_factor_method_sms', { defaultValue: 'SMS code' }),
        hint: t('two_factor_method_sms_help', { defaultValue: 'Receive a one-time code on your phone.' }),
    },
});

const BENEFITS = (t) => [
    {
        icon: 'lock-closed',
        title: t('two_factor_benefit_protect_title', { defaultValue: 'Blocks 99.9% of attacks' }),
        text: t('two_factor_benefit_protect_text', { defaultValue: 'Even if your password leaks, nobody can sign in without your second factor.' }),
    },
    {
        icon: 'flash',
        title: t('two_factor_benefit_fast_title', { defaultValue: 'Fast & frictionless' }),
        text: t('two_factor_benefit_fast_text', { defaultValue: 'A 6-digit code takes seconds. Trusted devices skip it for 30 days.' }),
    },
    {
        icon: 'business',
        title: t('two_factor_benefit_compliance_title', { defaultValue: 'Recommended & compliant' }),
        text: t('two_factor_benefit_compliance_text', { defaultValue: 'Meets modern security best practices for handling business data.' }),
    },
];

export default function TwoFactorEnrollmentModal({
    visible,
    token,
    t,
    mustEnroll,
    onCompleted,
    onSkip,
}) {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [method, setMethod] = useState(null);
    const [code, setCode] = useState('');
    const [phone, setPhone] = useState('');
    const [totpSetupData, setTotpSetupData] = useState(null);
    const [showManualKey, setShowManualKey] = useState(false);
    const [keyCopied, setKeyCopied] = useState(false);
    const [recoveryCodes, setRecoveryCodes] = useState(null);
    const [error, setError] = useState('');
    // Masked e-mail the SMS setup code was sent to instead, when the SMS
    // provider is degraded (smsSetup response smsDegraded:true). Null otherwise.
    const [smsFallbackEmail, setSmsFallbackEmail] = useState(null);

    const labels = useMemo(() => METHOD_META(t), [t]);
    const benefits = useMemo(() => BENEFITS(t), [t]);
    const manualSecret = useMemo(() => extractTotpSecret(totpSetupData?.otpauth), [totpSetupData]);

    const copyManualKey = async () => {
        if (!manualSecret) {
            return;
        }
        try {
            await Clipboard.setStringAsync(manualSecret);
            setKeyCopied(true);
            setTimeout(() => setKeyCopied(false), 2200);
        } catch (_e) {
            // Clipboard unavailable — the key stays selectable on screen.
        }
    };

    const refreshStatus = async () => {
        if (!token) {
            return;
        }
        setLoading(true);
        setError('');
        try {
            const doc = await twoFactorAuth.getStatus(token);

            setStatus(doc);
            if (doc?.enabled) {
                onCompleted?.();
            }
        } catch (e) {
            setError(e?.response?.data?.message || e?.message || t('two_factor_status_failed', { defaultValue: 'Could not load 2FA status.' }));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (visible) {
            setMethod(null);
            setCode('');
            setPhone('');
            setTotpSetupData(null);
            setShowManualKey(false);
            setRecoveryCodes(null);
            setSmsFallbackEmail(null);
            refreshStatus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, token]);

    const startMethod = async (nextMethod) => {
        setMethod(nextMethod);
        setCode('');
        setError('');
        setSmsFallbackEmail(null);
        setLoading(true);
        try {
            if (nextMethod === 'totp') {
                const data = await twoFactorAuth.totpSetup(token);

                setTotpSetupData(data);
            }
            if (nextMethod === 'email') {
                await twoFactorAuth.emailSend(token);
            }
            if (nextMethod === 'sms') {
                setPhone(status?.methods?.sms?.phone || '');
            }
        } catch (e) {
            setError(e?.response?.data?.message || e?.message || t('two_factor_setup_failed', { defaultValue: 'Setup failed.' }));
        } finally {
            setLoading(false);
        }
    };

    const submitCurrentMethod = async () => {
        if (!method) {
            return;
        }

        setLoading(true);
        setError('');
        try {
            let res = null;

            if (method === 'totp') {
                res = await twoFactorAuth.totpVerify(token, String(code || '').trim());
            }
            if (method === 'email') {
                res = await twoFactorAuth.emailVerify(token, String(code || '').trim());
            }
            if (method === 'sms') {
                if (!status?.methods?.sms?.enabled || phone) {
                    const setupRes = await twoFactorAuth.smsSetup(token, String(phone || '').trim());

                    setSmsFallbackEmail(setupRes?.smsDegraded ? (setupRes?.hint?.email || '') : null);
                }
                res = await twoFactorAuth.smsVerify(token, String(code || '').trim());
            }

            // First-ever enrolment returns one-time recovery codes — show them
            // before completing so the user can save them. Otherwise finish.
            const codes = Array.isArray(res?.recoveryCodes) ? res.recoveryCodes : null;

            if (codes && codes.length) {
                setRecoveryCodes(codes);
                setMethod(null);
                setCode('');
                setTotpSetupData(null);
                setShowManualKey(false);
                setSmsFallbackEmail(null);
            } else {
                await refreshStatus();
                setMethod(null);
                setCode('');
                setTotpSetupData(null);
                setShowManualKey(false);
                setSmsFallbackEmail(null);
            }
        } catch (e) {
            setError(e?.response?.data?.message || e?.message || t('two_factor_verify_failed', { defaultValue: 'Verification failed.' }));
        } finally {
            setLoading(false);
        }
    };

    const finishRecoveryStep = async () => {
        setRecoveryCodes(null);
        await refreshStatus();
    };

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={mustEnroll ? undefined : onSkip}>
            <ModalKeyboardAvoider style={styles.overlay} justify="center">
                <ScrollView
                    style={styles.flex}
                    contentContainerStyle={styles.scroll}
                    keyboardShouldPersistTaps="handled"
                    keyboardDismissMode="interactive"
                    showsVerticalScrollIndicator={false}
                >
                    <View style={styles.card}>
                        <View style={styles.header}>
                            <TwoFactorBrand icon="shield-checkmark" size={68} />
                            <Text style={styles.eyebrow}>{t('two_factor_secure_eyebrow', { defaultValue: 'Account security' })}</Text>
                            <Text style={styles.title}>
                                {t('two_factor_setup_title', { defaultValue: 'Turn on two-factor authentication' })}
                            </Text>
                            <Text style={styles.subtitle}>
                                {mustEnroll
                                    ? t('two_factor_setup_required', { defaultValue: 'Your organization requires at least one 2FA method before continuing.' })
                                    : t('two_factor_setup_optional', { defaultValue: 'Add a second layer of protection in under a minute.' })}
                            </Text>
                        </View>

                        {!recoveryCodes && !method && (
                            <>
                                <View style={styles.benefits}>
                                    {benefits.map((b) => (
                                        <View key={b.icon} style={styles.benefitRow}>
                                            <View style={styles.benefitIcon}>
                                                <Ionicons name={b.icon} size={18} color={COLORS.brandDark} />
                                            </View>
                                            <View style={{ flex: 1 }}>
                                                <Text style={styles.benefitTitle}>{b.title}</Text>
                                                <Text style={styles.benefitText}>{b.text}</Text>
                                            </View>
                                        </View>
                                    ))}
                                </View>

                                <Text style={styles.sectionLabel}>
                                    {t('two_factor_choose_method', { defaultValue: 'Choose how to verify' })}
                                </Text>
                                {METHODS.map((item) => {
                                    const meta = labels[item];
                                    const enabled = status?.methods?.[item]?.enabled;

                                    return (
                                        <TouchableOpacity
                                            key={item}
                                            style={styles.methodCard}
                                            onPress={() => startMethod(item)}
                                            disabled={loading}
                                            activeOpacity={0.85}
                                        >
                                            <View style={styles.methodIcon}>
                                                <Ionicons name={meta.icon} size={20} color={COLORS.ink} />
                                            </View>
                                            <View style={{ flex: 1 }}>
                                                <Text style={styles.methodLabel}>{meta.label}</Text>
                                                <Text style={styles.methodHint}>{meta.hint}</Text>
                                            </View>
                                            {enabled
                                                ? (
                                                    <View style={styles.enabledBadge}>
                                                        <Ionicons name="checkmark-circle" size={14} color={COLORS.success} />
                                                        <Text style={styles.enabledText}>{t('enabled', { defaultValue: 'Enabled' })}</Text>
                                                    </View>
                                                )
                                                : <Ionicons name="chevron-forward" size={18} color="#94A3B8" />}
                                        </TouchableOpacity>
                                    );
                                })}
                            </>
                        )}

                        {!!recoveryCodes && (
                            <RecoveryCodes
                                codes={recoveryCodes}
                                t={t}
                                onDone={finishRecoveryStep}
                                busy={loading}
                            />
                        )}

                        {!recoveryCodes && !!method && (
                            <View style={styles.methodPanel}>
                                <TouchableOpacity onPress={() => { setMethod(null); setError(''); }} disabled={loading} style={styles.backRow}>
                                    <Ionicons name="arrow-back" size={16} color={COLORS.brandDark} />
                                    <Text style={styles.back}>{t('two_factor_change_method', { defaultValue: 'Change method' })}</Text>
                                </TouchableOpacity>
                                <Text style={styles.methodTitle}>{labels[method].label}</Text>

                                {method === 'totp' && totpSetupData?.qrDataUrl && (
                                    <>
                                        <Text style={styles.helpText}>
                                            {t('two_factor_totp_scan', { defaultValue: 'Scan this QR code with your authenticator app, then enter the 6-digit code.' })}
                                        </Text>
                                        <Image source={{ uri: totpSetupData.qrDataUrl }} style={styles.qrImage} />

                                        {!!manualSecret && (
                                            <View style={styles.manualWrap}>
                                                <TouchableOpacity
                                                    onPress={() => setShowManualKey((v) => !v)}
                                                    activeOpacity={0.8}
                                                    style={styles.manualToggle}
                                                >
                                                    <Ionicons name="key-outline" size={15} color={COLORS.brandDark} />
                                                    <Text style={styles.manualToggleText}>
                                                        {t('two_factor_totp_cant_scan', { defaultValue: "Can't scan? Enter the key manually" })}
                                                    </Text>
                                                    <Ionicons name={showManualKey ? 'chevron-up' : 'chevron-down'} size={15} color={COLORS.brandDark} />
                                                </TouchableOpacity>

                                                {showManualKey && (
                                                    <View style={styles.manualBox}>
                                                        <Text style={styles.manualLabel}>
                                                            {t('two_factor_totp_setup_key', { defaultValue: 'Setup key' })}
                                                        </Text>
                                                        <Text style={styles.manualKey} selectable>
                                                            {formatSecretForDisplay(manualSecret)}
                                                        </Text>
                                                        <TouchableOpacity style={styles.manualCopy} onPress={copyManualKey} activeOpacity={0.85}>
                                                            <Ionicons name={keyCopied ? 'checkmark-circle' : 'copy-outline'} size={15} color={keyCopied ? COLORS.success : COLORS.ink} />
                                                            <Text style={[styles.manualCopyText, keyCopied ? { color: COLORS.success } : null]}>
                                                                {keyCopied ? t('copied', { defaultValue: 'Copied' }) : t('two_factor_totp_copy_key', { defaultValue: 'Copy key' })}
                                                            </Text>
                                                        </TouchableOpacity>
                                                        <Text style={styles.manualMeta}>
                                                            {t('two_factor_totp_manual_meta', {
                                                                defaultValue: 'Time-based · 6 digits · 30 seconds',
                                                            })}
                                                        </Text>
                                                    </View>
                                                )}
                                            </View>
                                        )}
                                    </>
                                )}
                                {method === 'email' && (
                                    <Text style={styles.helpText}>
                                        {t('two_factor_email_sent_help', { defaultValue: 'We emailed you a 6-digit code. Enter it below.' })}
                                    </Text>
                                )}
                                {method === 'sms' && (
                                    <TextInput
                                        value={phone}
                                        onChangeText={setPhone}
                                        style={styles.input}
                                        placeholder={t('two_factor_phone_placeholder', { defaultValue: '+36 70 329 8239' })}
                                        placeholderTextColor={COLORS.sub}
                                        keyboardType="phone-pad"
                                    />
                                )}

                                {method === 'sms' && smsFallbackEmail !== null && (
                                    <View style={styles.warningRow}>
                                        <Ionicons name="alert-circle-outline" size={15} color={COLORS.brandDark} />
                                        <Text style={styles.warning}>
                                            {t('two_factor_sms_fallback_email', {
                                                defaultValue: "SMS service issue — we've e-mailed your code to {{hint}} instead. Please check your mailbox (and spam folder).",
                                                hint: smsFallbackEmail || '•••',
                                            })}
                                        </Text>
                                    </View>
                                )}

                                <View style={styles.otpWrap}>
                                    <OtpInput value={code} onChange={setCode} length={6} disabled={loading} autoFocus={method !== 'sms'} />
                                </View>

                                <TouchableOpacity
                                    style={[styles.primary, loading ? styles.primaryDisabled : null]}
                                    onPress={submitCurrentMethod}
                                    disabled={loading}
                                    activeOpacity={0.9}
                                >
                                    {loading
                                        ? <ActivityIndicator color="#fff" />
                                        : <Text style={styles.primaryText}>{t('two_factor_verify_and_enable', { defaultValue: 'Verify and enable' })}</Text>}
                                </TouchableOpacity>
                            </View>
                        )}

                        {loading && !method && <ActivityIndicator style={{ marginTop: 14 }} color={COLORS.brand} />}

                        {!!error && (
                            <View style={styles.errorRow}>
                                <Ionicons name="alert-circle" size={15} color={COLORS.danger} />
                                <Text style={styles.error}>{error}</Text>
                            </View>
                        )}

                        {!mustEnroll && !method && !recoveryCodes && (
                            <TouchableOpacity onPress={onSkip} disabled={loading} style={styles.skipBtn}>
                                <Text style={styles.skip}>{t('two_factor_skip_for_now', { defaultValue: 'Skip for now' })}</Text>
                            </TouchableOpacity>
                        )}
                    </View>
                </ScrollView>
            </ModalKeyboardAvoider>
        </Modal>
    );
}

const styles = StyleSheet.create({
    flex: {
        flex: 1,
    },
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(2, 6, 23, 0.6)',
    },
    scroll: {
        flexGrow: 1,
        justifyContent: 'center',
        padding: 16,
    },
    card: {
        backgroundColor: '#fff',
        borderRadius: 26,
        padding: 22,
        shadowColor: '#0F172A',
        shadowOpacity: 0.2,
        shadowRadius: 32,
        shadowOffset: { width: 0, height: 18 },
        elevation: 12,
    },
    header: {
        alignItems: 'center',
        marginBottom: 16,
    },
    eyebrow: {
        marginTop: 14,
        fontSize: 11,
        fontWeight: '700',
        letterSpacing: 1,
        textTransform: 'uppercase',
        color: COLORS.brandDark,
    },
    title: {
        marginTop: 4,
        fontSize: 21,
        fontWeight: '800',
        color: COLORS.ink,
        textAlign: 'center',
    },
    subtitle: {
        marginTop: 8,
        fontSize: 13,
        lineHeight: 19,
        color: COLORS.sub,
        textAlign: 'center',
    },
    benefits: {
        gap: 12,
        marginBottom: 18,
    },
    benefitRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 12,
    },
    benefitIcon: {
        width: 36,
        height: 36,
        borderRadius: 10,
        backgroundColor: '#FFF6E6',
        alignItems: 'center',
        justifyContent: 'center',
    },
    benefitTitle: {
        fontSize: 14,
        fontWeight: '700',
        color: COLORS.ink,
    },
    benefitText: {
        marginTop: 2,
        fontSize: 12,
        lineHeight: 17,
        color: COLORS.sub,
    },
    sectionLabel: {
        fontSize: 12,
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        color: COLORS.sub,
        marginBottom: 10,
    },
    methodCard: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        borderWidth: 1,
        borderColor: COLORS.line,
        borderRadius: 14,
        padding: 14,
        marginBottom: 10,
    },
    methodIcon: {
        width: 40,
        height: 40,
        borderRadius: 11,
        backgroundColor: COLORS.soft,
        alignItems: 'center',
        justifyContent: 'center',
    },
    methodLabel: {
        fontSize: 15,
        fontWeight: '700',
        color: COLORS.ink,
    },
    methodHint: {
        marginTop: 2,
        fontSize: 12,
        color: COLORS.sub,
    },
    enabledBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingVertical: 4,
        paddingHorizontal: 8,
        borderRadius: 999,
        backgroundColor: '#DCFCE7',
    },
    enabledText: {
        fontSize: 11,
        fontWeight: '700',
        color: COLORS.success,
    },
    methodPanel: {
        marginTop: 4,
    },
    backRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        marginBottom: 12,
    },
    back: {
        color: COLORS.brandDark,
        fontWeight: '700',
    },
    methodTitle: {
        fontSize: 17,
        fontWeight: '800',
        color: COLORS.ink,
        marginBottom: 8,
    },
    helpText: {
        fontSize: 13,
        lineHeight: 19,
        color: COLORS.sub,
        marginBottom: 12,
    },
    qrImage: {
        width: 190,
        height: 190,
        alignSelf: 'center',
        marginBottom: 16,
        borderRadius: 12,
    },
    manualWrap: {
        marginBottom: 16,
    },
    manualToggle: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        paddingVertical: 8,
    },
    manualToggleText: {
        fontSize: 13,
        fontWeight: '700',
        color: COLORS.brandDark,
    },
    manualBox: {
        marginTop: 8,
        backgroundColor: COLORS.soft,
        borderWidth: 1,
        borderColor: COLORS.line,
        borderRadius: 14,
        padding: 14,
        alignItems: 'center',
    },
    manualLabel: {
        fontSize: 11,
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        color: COLORS.sub,
    },
    manualKey: {
        marginTop: 6,
        fontSize: 16,
        fontWeight: '800',
        letterSpacing: 2,
        color: COLORS.ink,
        textAlign: 'center',
        fontVariant: ['tabular-nums'],
    },
    manualCopy: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        marginTop: 12,
        paddingVertical: 8,
        paddingHorizontal: 16,
        borderRadius: 999,
        borderWidth: 1.5,
        borderColor: COLORS.line,
        backgroundColor: '#fff',
    },
    manualCopyText: {
        fontSize: 13,
        fontWeight: '700',
        color: COLORS.ink,
    },
    manualMeta: {
        marginTop: 10,
        fontSize: 11.5,
        color: COLORS.sub,
        textAlign: 'center',
    },
    input: {
        borderWidth: 1.5,
        borderColor: COLORS.line,
        borderRadius: 12,
        paddingHorizontal: 14,
        paddingVertical: 12,
        marginBottom: 14,
        color: COLORS.ink,
        fontSize: 16,
    },
    otpWrap: {
        alignItems: 'center',
        marginBottom: 18,
    },
    primary: {
        backgroundColor: COLORS.ink,
        borderRadius: 14,
        paddingVertical: 15,
        alignItems: 'center',
    },
    primaryDisabled: {
        opacity: 0.6,
    },
    primaryText: {
        color: '#fff',
        fontWeight: '800',
        fontSize: 15,
    },
    errorRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        marginTop: 14,
    },
    error: {
        color: COLORS.danger,
        fontSize: 13,
        flex: 1,
    },
    warningRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 6,
        marginTop: 10,
        marginBottom: 4,
    },
    warning: {
        color: COLORS.brandDark,
        fontSize: 12.5,
        lineHeight: 17,
        flex: 1,
    },
    skipBtn: {
        marginTop: 14,
        alignItems: 'center',
        paddingVertical: 10,
    },
    skip: {
        color: COLORS.sub,
        fontWeight: '600',
        fontSize: 14,
    },
});
